import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { 
    Calendar as CalendarIcon, 
    Send, 
    CheckCircle, 
    Clock, 
    MapPin, 
    Phone,
    Mail,
    Music,
    Users,
    IndianRupee,
    Sparkles,
    Star
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const eventTypes = [
    { value: 'wedding', label: 'Wedding' },
    { value: 'corporate', label: 'Corporate Event' },
    { value: 'birthday', label: 'Birthday Party' },
    { value: 'anniversary', label: 'Anniversary' },
    { value: 'sangeet', label: 'Sangeet/Mehendi' },
    { value: 'college', label: 'College Fest' },
    { value: 'concert', label: 'Concert/Festival' },
    { value: 'other', label: 'Other' },
];

const budgetRanges = [
    { value: 'under-50k', label: 'Under ₹50,000' },
    { value: '50k-1l', label: '₹50,000 - ₹1,00,000' },
    { value: '1l-2l', label: '₹1,00,000 - ₹2,00,000' },
    { value: '2l-5l', label: '₹2,00,000 - ₹5,00,000' },
    { value: 'above-5l', label: 'Above ₹5,00,000' },
];

const guestCounts = [
    { value: 'under-100', label: 'Under 100' },
    { value: '100-300', label: '100 - 300' },
    { value: '300-500', label: '300 - 500' },
    { value: '500-1000', label: '500 - 1000' },
    { value: 'above-1000', label: 'Above 1000' },
];

const BookingPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        eventType: '',
        eventDate: null,
        venue: '',
        guestCount: '',
        budgetRange: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleDateSelect = (date) => {
        setFormData((prev) => ({ ...prev, eventDate: date }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1500));

        setIsSubmitting(false);
        setIsSubmitted(true);
        toast.success('Booking request submitted successfully!', {
            description: "We'll get back to you within 24 hours.",
        });
    };

    if (isSubmitted) {
        return (
            <main className="min-h-screen pt-24 pb-16">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="max-w-2xl mx-auto text-center py-20"
                    >
                        <div className="w-20 h-20 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-10 h-10 text-success" />
                        </div>
                        <h2 className="font-heading font-bold text-3xl text-foreground mb-4">
                            Booking Request Received!
                        </h2>
                        <p className="text-muted-foreground font-body mb-6">
                            Thank you for choosing Hawayein The Band! Our team will review your request 
                            and get back to you within 24 hours with availability and pricing details.
                        </p>
                        <Button
                            onClick={() => {
                                setIsSubmitted(false);
                                setFormData({
                                    name: '',
                                    email: '',
                                    phone: '',
                                    eventType: '',
                                    eventDate: null,
                                    venue: '',
                                    guestCount: '',
                                    budgetRange: '',
                                    message: '',
                                });
                            }}
                            className="bg-gradient-to-r from-primary to-accent text-foreground font-heading font-semibold rounded-xl"
                        >
                            Submit Another Request
                        </Button>
                    </motion.div>
                </div>
            </main>
        );
    }

    return (
        <main className="min-h-screen pt-24 pb-16 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Page Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <Badge 
                        variant="outline" 
                        className="mb-4 px-4 py-1.5 border-primary/40 bg-primary/10 text-primary font-body"
                    >
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                        Book Your Event
                    </Badge>
                    <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
                        Let's Create Magic Together
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg font-body max-w-2xl mx-auto">
                        Fill in the details below and our team will get back to you with a personalized quote
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {/* Booking Form */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="lg:col-span-2"
                    >
                        <Card className="card-premium rounded-2xl">
                            <CardHeader className="pb-6">
                                <CardTitle className="font-heading text-xl text-foreground">
                                    Event Details
                                </CardTitle>
                                <CardDescription className="font-body">
                                    Tell us about your event and we'll customize our performance for you
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Personal Info */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="name" className="font-body text-foreground">
                                                Full Name *
                                            </Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                placeholder="Your full name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="bg-muted/50 border-border/50"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="phone" className="font-body text-foreground">
                                                Phone Number *
                                            </Label>
                                            <Input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                placeholder="+91 98765 43210"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                required
                                                className="bg-muted/50 border-border/50"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="email" className="font-body text-foreground">
                                            Email Address *
                                        </Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="your@email.com"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            className="bg-muted/50 border-border/50"
                                        />
                                    </div>

                                    {/* Event Type & Date */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="font-body text-foreground">
                                                Event Type *
                                            </Label>
                                            <Select
                                                value={formData.eventType}
                                                onValueChange={(value) => handleSelectChange('eventType', value)}
                                            >
                                                <SelectTrigger className="bg-muted/50 border-border/50">
                                                    <SelectValue placeholder="Select event type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {eventTypes.map((type) => (
                                                        <SelectItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="font-body text-foreground">
                                                Event Date *
                                            </Label>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        className="w-full justify-start text-left font-normal bg-muted/50 border-border/50 hover:bg-muted"
                                                    >
                                                        <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                                                        {formData.eventDate ? (
                                                            format(formData.eventDate, 'PPP')
                                                        ) : (
                                                            <span className="text-muted-foreground">Pick a date</span>
                                                        )}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0" align="start">
                                                    <Calendar
                                                        mode="single"
                                                        selected={formData.eventDate}
                                                        onSelect={handleDateSelect}
                                                        disabled={(date) => date < new Date()}
                                                        initialFocus
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                    </div>

                                    {/* Venue */}
                                    <div className="space-y-2">
                                        <Label htmlFor="venue" className="font-body text-foreground">
                                            Venue / Location *
                                        </Label>
                                        <Input
                                            id="venue"
                                            name="venue"
                                            placeholder="Event venue or city"
                                            value={formData.venue}
                                            onChange={handleInputChange}
                                            required
                                            className="bg-muted/50 border-border/50"
                                        />
                                    </div>

                                    {/* Guest Count & Budget */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="font-body text-foreground">
                                                Expected Guests
                                            </Label>
                                            <Select
                                                value={formData.guestCount}
                                                onValueChange={(value) => handleSelectChange('guestCount', value)}
                                            >
                                                <SelectTrigger className="bg-muted/50 border-border/50">
                                                    <SelectValue placeholder="Select guest count" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {guestCounts.map((count) => (
                                                        <SelectItem key={count.value} value={count.value}>
                                                            {count.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="font-body text-foreground">
                                                Budget Range
                                            </Label>
                                            <Select
                                                value={formData.budgetRange}
                                                onValueChange={(value) => handleSelectChange('budgetRange', value)}
                                            >
                                                <SelectTrigger className="bg-muted/50 border-border/50">
                                                    <SelectValue placeholder="Select budget range" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {budgetRanges.map((range) => (
                                                        <SelectItem key={range.value} value={range.value}>
                                                            {range.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    {/* Message */}
                                    <div className="space-y-2">
                                        <Label htmlFor="message" className="font-body text-foreground">
                                            Additional Requirements
                                        </Label>
                                        <Textarea
                                            id="message"
                                            name="message"
                                            placeholder="Tell us about any specific songs, themes, or requirements for your event..."
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            rows={4}
                                            className="bg-muted/50 border-border/50 resize-none"
                                        />
                                    </div>

                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full btn-glow bg-gradient-to-r from-primary to-accent text-foreground font-heading font-semibold py-6 rounded-xl text-base"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="animate-spin mr-2">⏳</span>
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-5 h-5 mr-2" />
                                                Submit Booking Request
                                            </>
                                        )}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Sidebar */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="space-y-6"
                    >
                        {/* Quick Contact */}
                        <Card className="card-premium rounded-2xl">
                            <CardContent className="p-6">
                                <h3 className="font-heading font-semibold text-lg text-foreground mb-4">
                                    Quick Contact
                                </h3>
                                <div className="space-y-4">
                                    <a 
                                        href="tel:+917000312302"
                                        className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <Phone className="w-4 h-4 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-body">Call Us</p>
                                            <p className="text-foreground font-medium">+91 7000312302</p>
                                        </div>
                                    </a>
                                    <a 
                                        href="mailto:musicalyash92@gmail.com"
                                        className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                                            <Mail className="w-4 h-4 text-accent" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-body">Email Us</p>
                                            <p className="text-foreground font-medium">musicalyash92@gmail.com</p>
                                        </div>
                                    </a>
                                </div>
                            </CardContent>
                        </Card>

                        {/* What's Included */}
                        <Card className="card-premium rounded-2xl">
                            <CardContent className="p-6">
                                <h3 className="font-heading font-semibold text-lg text-foreground mb-4">
                                    What's Included
                                </h3>
                                <ul className="space-y-3">
                                    {[
                                        { icon: Music, text: 'Full Band Performance' },
                                        { icon: Clock, text: '3-4 Hours Live Show' },
                                        { icon: Users, text: 'Professional Sound Team' },
                                        { icon: MapPin, text: 'Pan-India Coverage' },
                                        { icon: Star, text: 'Custom Song Requests' },
                                    ].map((item, index) => (
                                        <li key={index} className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
                                                <item.icon className="w-4 h-4 text-primary" />
                                            </div>
                                            <span className="text-sm font-body text-muted-foreground">
                                                {item.text}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>

                        {/* Response Time */}
                        <Card className="card-premium rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
                            <CardContent className="p-6 text-center">
                                <Clock className="w-10 h-10 text-primary mx-auto mb-3" />
                                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                                    Quick Response
                                </h3>
                                <p className="text-sm font-body text-muted-foreground">
                                    We respond to all inquiries within <span className="text-primary font-semibold">24 hours</span>
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </div>
        </main>
    );
};

export default BookingPage;
